import isBoolean from 'lodash/isBoolean'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _join from 'lodash/join'
import isNumber from 'lodash/isNumber'
import * as Yup from 'yup'
import _omitBy from 'lodash/omitBy'
import _isUndefined from 'lodash/isUndefined'
import { fromJS, List, Map } from 'immutable'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import { formatDateWithTimezonOffset, formatWrapped } from '../date'
import matchPath from '../../routes/match-path'
import { createAuthorization } from '../auth'
import { SORT_ORDER_ASC } from '../constants'
import { USD, formatAmount } from '../currency'
import { getConfigDataServer } from '../config'
import { US } from '../countries'
import request from '../request'
import { getLocationPathname, createLocationHref, getLocationQuery } from '../location'
import { exportCSVFile, getCSVNumberValue } from '../file'

// Config to Eng by default
countries.registerLocale(enLocale)

/**
 * The ambassador store key
 */
export const AMBASSADOR_STORE_KEY = 'ambassador'

/**
 * The ambassador store key
 */
export const CAMPAIGN_STORE_KEY = 'campaign'

/**
 * The ambassador history payout store key
 */
export const AMBASSADOR_HISTORY_STORE_KEY = 'ambassadorHistory'

/**
 * The ambassador payout store key
 */
export const AMBASSADOR_PAYOUT_STORE_KEY = 'ambassadorPayout'

/**
 * The ambassador campaigns store key
 */
export const AMBASSADOR_CAMPAIGNS_STORE_KEY = 'ambassadorCampaigns'

/**
 * The ambassador leads store key
 */
export const AMBASSADOR_LEADS_STORE_KEY = 'ambassadorLeads'

/**
 * The ambassador campaigns store key
 */
export const AMBASSADOR_CAMPAIGN_DETAILS_STORE_KEY = 'ambassadorCampaignDetails'

/**
 * The ambassador signups store key
 */
export const AMBASSADOR_SIGNUPS_STORE_KEY = 'ambassadorSignups'

/**
 * The ambassador conversions store key
 */
export const AMBASSADOR_CONVERSIONS_STORE_KEY = 'ambassadorConversions'

/**
 * The ambassador payouts store key
 */
export const AMBASSADOR_PAYOUTS_STORE_KEY = 'ambassadorPayouts'

/**
 * The ambassador admin store key
 */
export const AMBASSADOR_ADMIN_STORE_KEY = 'ambassadorAdmin'

/**
 * The ambassador admin store key
 */
export const AMBASSADOR_RELATIONSHIP_OWNER_STORE_KEY = 'ambassadorRelationshipOwner'

/**
 * The ambassador create application key
 */
export const AMBASSADOR_CREATE_APPLICATION_KEY = 'create'

/**
 * The campaign create application key
 */
export const CAMPAIGN_CREATE_APPLICATION_KEY = 'create'

/**
 * The campaign create application key
 */
export const CAMPAIGN_UPDATE_APPLICATION_KEY = 'update'

/**
 * The ambassador update application key
 */
export const AMBASSADOR_UPDATE_APPLICATION_KEY = 'update'

/**
 * The ambassador user search application key
 */
export const AMBASSADOR_USER_SEARCH_APPLICATION_KEY = 'userSearch'

/**
 * The ambassador payout totals application key
 */
export const AMBASSADOR_PAYOUT_TOTALS_APPLICATION_KEY = 'ambassadorPayoutTotals'

/**
 * The ambassador payout CSV application key
 */
export const AMBASSADOR_PAYOUTS_CSV_APPLICATION_KEY = 'ambassadorPayoutCSV'

/**
 * The ambassador list CSV application key
 */
export const AMBASSADOR_LIST_CSV_APPLICATION_KEY = 'ambassadorListCSV'

/**
 * The ambassadors signups CSV application key
 */
export const AMBASSADOR_SIGNUPS_CSV_APPLICATION_KEY = 'ambassadorSignupsCSV'

/**
 * The ambassadors conversions CSV application key
 */
export const AMBASSADOR_CONVERSIONS_CSV_APPLICATION_KEY = 'ambassadorConversionsCSV'

/**
 * The ambassadors leads CSV application key
 */
export const AMBASSADOR_LEADS_CSV_APPLICATION_KEY = 'ambassadorLeadsCSV'

/**
 * The ambassador parent application key
 */
export const AMBASSADOR_PARENT_APPLICATION_KEY = 'ambassadorParent'

/**
 * The ambassador parent application key
 */
export const AMBASSADOR_PARTNER_OPTIONS_KEY = 'ambassadorPartnerOptions'

/**
 * The ambassador items per page
 */
export const AMBASSADOR_ITEMS_PER_PAGE = 20

// Ambassador tabs
export const AMBASSADOR_CAMPAIGNS_TAB = 'campaigns'
export const CAMPAIGN_PARTNERS_TAB = 'partners'
export const LEADS_TAB = 'leads'
export const SIGNUPS_TAB = 'signups'
export const BILLINGS_TAB = 'billings'

// ambassador field names
export const AMBASSADOR_FIELD_NAME = 'name'
export const AMBASSADOR_FIELD_CONTACT_EMAIL = 'contactEmail'
export const AMBASSADOR_FIELD_UTM_MEDIUM = 'utmMedium'
export const AMBASSADOR_FIELD_PARENT_UTM = 'parentUtm'
export const AMBASSADOR_FIELD_IS_ACTIVE = 'isActive'
export const AMBASSADOR_FIELD_BUSINESS_NAME = 'businessName'
export const AMBASSADOR_FIELD_GAIA_EMAIL = 'gaiaEmail'
export const AMBASSADOR_FIELD_RELATIONSHIP_OWNER_UUID = 'relationshipOwnerUuid'

// Campaign field names
export const CAMPAIGN_FIELD_NAME = 'campaignName'
export const CAMPAIGN_FIELD_PARTNER_NAME = 'ambassadorName'
export const CAMPAIGN_FIELD_CLICKS = 'clicks'
export const CAMPAIGN_FIELD_LEADS = 'leads'
export const CAMPAIGN_FIELD_SIGNUPS = 'signups'
export const CAMPAIGN_FIELD_BILLINGS = 'payments'
export const CAMPAIGN_FIELD_REVENUE = 'totalBillingsUsd'
export const CAMPAIGN_FIELD_EARNINGS_PER_CLICK = 'earningsPerClick'
export const CAMPAIGN_FIELD_EARNINGS_PER_LEADS = 'earningsPerLead'
export const CAMPAIGN_FIELD_CLICK_TO_LEAD = 'leadConversionRate'
export const CAMPAIGN_FIELD_LEAD_TO_SIGNUP = 'signupConversionRate'
export const CAMPAIGN_FIELD_EARNINGS = 'earnings'

// Leads field names
export const LEAD_FIELD_NAME = 'email'
export const LEAD_REFERRING_PARTNER = 'ambassador'
export const LEAD_CAPTURE_DATE = 'createdAt'
export const LEAD_CONVERTED = 'converted'
export const LEAD_CONVERSION_DATE = 'conversionDate'
export const LEAD_CAMPAIGN = 'campaign'

// Signups field names
export const SIGNUPS_FIELD_DATE = 'createdAt'
export const SIGNUPS_FIELD_PARTNER = 'ambassador'
export const SIGNUPS_FIELD_MEMBER = 'member'
export const SIGNUPS_FIELD_PLAN = 'planName'
export const SIGNUPS_FIELD_EVENT = 'triggerEvent'
export const SIGNUPS_FIELD_CAMPAIGN = 'campaign'
export const SIGNUPS_FIELD_SOURCE = 'source'
export const SIGNUPS_FIELD_SOURCE_ID = 'medium'

// Billings field names
export const BILLINGS_FIELD_DATE = 'createdAt'
export const BILLINGS_FIELD_PARTNER = 'ambassador'
export const BILLINGS_FIELD_MEMBER = 'member'
export const BILLINGS_FIELD_PLAN = 'planName'
export const BILLINGS_FIELD_EVENT = 'triggerEvent'
export const BILLINGS_FIELD_CAMPAIGN = 'campaign'
export const BILLINGS_FIELD_PAYMENT = 'paymentAmount'
export const BILLINGS_FIELD_CURRENCY = 'paymentCurrency'
export const BILLINGS_FIELD_SOURCE = 'source'
export const BILLINGS_FIELD_SOURCE_ID = 'medium'

export const AMBASSADORS_START_YEAR = 2021
export const AMBASSADORS_START_MONTH_INDEX = 0 // January
export const AMBASSADORS_START_DATE = formatWrapped(
  new Date(AMBASSADORS_START_YEAR, AMBASSADORS_START_MONTH_INDEX),
  'yyyy-MM-dd',
)
export const AMBASSADOR_MAX_START_DATE = formatWrapped(new Date(), 'yyyy-MM-dd')
export const AMBASSADOR_ACTIVE = '1'
export const AMBASSADOR_INACTIVE = '0'
export const AMBASSADOR_COUNTRY_DEFAULT = US
export const AMBASSADOR_CURRENCY_DEFAULT = USD
export const AMBASSADOR_MAX_CSV_RECORDS = 50000
export const AMBASSADOR_CSV_BATCH_SIZE = 1000

const currentDate = new Date()
export const currentYear = currentDate.getFullYear()
export const nextYear = new Date(new Date().setFullYear(currentYear + 1)).getFullYear()

// Payout fields
export const PAYOUT_FIELD_RIPPLE_1_PERCENTAGE = 'ripple1Percentage'
export const PAYOUT_FIELD_RIPPLE_2_PERCENTAGE = 'ripple2Percentage'
export const PAYOUT_FIELD_UNPAID_FROM_PREVIOUS_MONTH_USD = 'unpaidFromPreviousMonthUSD'
export const PAYOUT_FIELD_RIPPLE_1_UTM_PAYOUT_USD = 'ripple1UtmPayoutUSD'
export const PAYOUT_FIELD_RIPPLE_1_REFERRAL_PAYOUT_USD = 'ripple1ReferralPayoutUSD'
export const PAYOUT_FIELD_RIPPLE_2_UTM_PAYOUT_USD = 'ripple2UtmPayoutUSD'
export const PAYOUT_FIELD_RIPPLE_2_REFERRAL_PAYOUT_USD = 'ripple2ReferralPayoutUSD'
export const PAYOUT_FIELD_TOTAL_PAYOUT_USD = 'totalPayoutUSD'
export const PAYOUT_FIELD_AMBASSADOR_IS_ACTIVE = 'ambassadorIsActive'

// Relationships
const AMBASSADOR_RELATIONSHIP_DEFAULT = 'Partner'
const AMBASSADOR_RELATIONSHIP_FMTV = 'Partner - FMTV'
const AMBASSADOR_RELATIONSHIP_YOGA_TEACHER = 'Partner - Gaia Yoga Teacher'
const AMBASSADOR_RELATIONSHIP_GUEST = 'Partner - Guest'
const AMBASSADOR_RELATIONSHIP_MDG = 'Partner - MDG'
const AMBASSADOR_RELATIONSHIP_YOUTUBE = 'Partner - YouTube'
const AMBASSADOR_RELATIONSHIP_HOST = 'Host'

export const AMBASSADOR_RELATIONSHIPS = [
  {
    name: AMBASSADOR_RELATIONSHIP_DEFAULT,
    label: 'Partner',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_FMTV,
    label: 'Partner - FMTV',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_YOGA_TEACHER,
    label: 'Partner - Gaia Yoga Teacher',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_GUEST,
    label: 'Partner - Guest',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_MDG,
    label: 'Partner - MDG',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_YOUTUBE,
    label: 'Partner - YouTube',
  },
  {
    name: AMBASSADOR_RELATIONSHIP_HOST,
    label: 'Host',
  },
]

export const DEFAULT_FORM_VALUES = {
  businessName: '',
  name: '',
  relationship: AMBASSADOR_RELATIONSHIP_DEFAULT,
  contactEmail: '',
  parentAmbassadorUuid: '',
  gaiaEmail: '',
  utmMedium: '',
  ripple1Percentage: '',
  ripple2Percentage: '',
  startedAt: AMBASSADOR_MAX_START_DATE,
  isActive: AMBASSADOR_ACTIVE,
  residenceCountry: AMBASSADOR_COUNTRY_DEFAULT,
  payoutCurrency: AMBASSADOR_CURRENCY_DEFAULT,
  relationshipOwner: 'Unassigned',
}

// Monthly payouts
export const PAYOUT_MIN = 300

/**
 * create month and year (current or last month)
 * we are doing this bc production does not show current
 * but stage does for testing
 * @param {Boolean} showCurrentMonth showCurrentMonth
 * @returns {Map}
 */
export function getMonthYear ({ showCurrentMonth }) {
  const date = new Date()
  let month = date.getMonth()
  let year = date.getFullYear()
  if (!showCurrentMonth) {
    month = month === 0 ? 11 : month - 1
    year = month === 11 ? year - 1 : year
  }
  return { month: month + 1, year }
}

const TIMEFRAMES = {
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
}

/**
 * create start and end dates from location
 * @param {String} timeframe timeframe
 * @param {String} month month
 * @param {String} year year
 * @returns {Array}
 */
function getStartAndEndDates ({ month, year, timeframe }) {
  const startDate = new Date()
  const endDate = new Date()
  const formatDate = (d) => formatWrapped(d, 'yyyy-MM-dd')

  if (year) {
    startDate.setFullYear(year)
    endDate.setFullYear(year)
  }

  if (month) {
    // subtracting one bc we start with index 1 in query
    // not 0 like dates need to use
    startDate.setMonth(month - 1)
  }

  if (timeframe === TIMEFRAMES.YEARLY) {
    startDate.setMonth(0)
    startDate.setDate(1)
    endDate.setMonth(12)
    endDate.setDate(0)
    return [formatDate(startDate), formatDate(endDate)]
  }

  if (timeframe === TIMEFRAMES.MONTHLY) {
    startDate.setDate(1)
    endDate.setMonth(startDate.getMonth() + 1)
    endDate.setDate(0)
    return [formatDate(startDate), formatDate(endDate)]
  }
  return [formatDate(startDate)]
}

/**
 * Get the values for the ambassador form
 * @param {Map} ambassador
 * @returns {object}
 */
export const getInitialFormValues = (ambassador, authUuid, ambassadorAdmins) => {
  let base = { ...DEFAULT_FORM_VALUES }
  const relationshipOwner = ambassador.getIn(['relationshipOwner', 'fullName'], undefined)
  const admin = ambassadorAdmins.find((aa) => aa.get('uuid') === authUuid)
  const suggestedRelationshipOwner = (admin && admin.get('uuid')) || 'Unassigned'
  base.relationshipOwner = relationshipOwner || suggestedRelationshipOwner

  if (ambassador && Map.isMap(ambassador) && !ambassador.isEmpty()) {
    const startDate = formatDateWithTimezonOffset(
      new Date(ambassador.get('startedAt', AMBASSADOR_MAX_START_DATE)),
    )
    base = {
      businessName: ambassador.get('businessName', ''),
      name: ambassador.get('name', ''),
      relationship: ambassador.get('relationship', AMBASSADOR_RELATIONSHIP_DEFAULT),
      contactEmail: ambassador.get('contactEmail', ''),
      parentAmbassadorUuid: ambassador.getIn(['parentAmbassador', 'utmMedium'], ''),
      gaiaEmail: ambassador.get('gaiaEmail', ''),
      utmMedium: ambassador.get('utmMedium', ''),
      everflowAffiliateId: ambassador.get('everflowAffiliateId', ''),
      ripple1Percentage: ambassador.get('ripple1Percentage', ''),
      ripple2Percentage: ambassador.get('ripple2Percentage', ''),
      startedAt: formatWrapped(startDate, 'yyyy-MM-dd'),
      isActive: ambassador.get('isActive') ? AMBASSADOR_ACTIVE : AMBASSADOR_INACTIVE,
      residenceCountry:
        countries.alpha3ToAlpha2(ambassador.get('residenceCountry')) || AMBASSADOR_COUNTRY_DEFAULT,
      payoutCurrency: ambassador.get('payoutCurrency', AMBASSADOR_CURRENCY_DEFAULT),
      relationshipOwner: ambassador.getIn(['relationshipOwner', 'uuid'], 'Unassigned'),
    }
  }
  return base
}

/**
 * Sets location with given params
 * @param {String} sortDirection sortDirection
 * @param {String} sortField sortField
 * @param {Date} startDate startDate
 * @param {Object} history history
 * @param {Map} location location
 * @param {Date} endDate endDate
 * @param {Number} month month
 * @param {Number} year year
 * @param {String} tab tab
 * @param {Number} p p
 * @returns {object}
 */

export function setLocation (params = {}) {
  const { history, location, tab, p, month, year, startDate, endDate, sortField, sortDirection } =
    params
  const currentQS = location.getIn(['data', 'query'])

  let qs = currentQS

  qs = tab ? qs.set('tab', tab) : qs.delete('tab', tab)
  qs = p ? qs.set('p', p) : qs.delete('p', p)
  qs = month ? qs.set('month', month) : qs.delete('month', month)
  qs = year ? qs.set('year', year) : qs.delete('year', year)
  qs = startDate ? qs.set('startDate', startDate) : qs.delete('startDate', startDate)
  qs = endDate ? qs.set('endDate', endDate) : qs.delete('endDate', endDate)
  qs = sortField ? qs.set('sortField', sortField) : qs.delete('sortField', sortField)
  qs = sortDirection
    ? qs.set('sortDirection', sortDirection)
    : qs.delete('sortDirection', sortDirection)

  history.replace(createLocationHref(location.setIn(['data', 'query'], qs)))
}

export const getValidationSchema = (auth, config, isEditing) =>
  Yup.object().shape({
    businessName: Yup.string().nullable(),
    name: Yup.string().required('Required'),
    relationship: Yup.string().required('Required'),
    contactEmail: Yup.string().email('Must be a valid email address').required('Required'),
    parentAmbassadorUuid: Yup.string().test({
      name: 'cannotReferSelf',
      message: 'Referring Partner cannot be self',
      test (value) {
        return value !== this.parent.utmMedium
      },
    }),
    gaiaEmail: Yup.string().email('Must be a valid email address').required('Required'),
    utmMedium: Yup.string()
      .required('Required')
      .test('checkUtmAvailable', 'This UTM Medium is already in the system', async (utmMedium) => {
        const server = getConfigDataServer(config)
        const ambassadorList = await getAmbassadorList({
          auth,
          server,
          utmMedium,
        })
        const utmIsAvailable = !ambassadorList
          .get('ambassadors')
          .find((a) => a.get('utmMedium') === utmMedium)
        const isValid = isEditing || utmIsAvailable
        return isValid
      }),
    ripple1Percentage: Yup.number()
      .min(1, 'Comission must be between 1 and 100')
      .max(100, 'Comission must be between 1 and 100')
      .required('Required'),
    ripple2Percentage: Yup.number()
      .min(1, 'Comission must be between 1 and 100')
      .max(100, 'Comission must be between 1 and 100')
      .required('Required'),
    startedAt: Yup.date().required('Required'),
    isActive: Yup.string().required('Required'),
    residenceCountry: Yup.string().required('Required'),
    payoutCurrency: Yup.string().required('Required'),
  })

export function searchInProgress (location = Map()) {
  const query = location.getIn(['data', 'query'], Map())
  return (
    query.has(AMBASSADOR_FIELD_NAME) ||
    query.has(AMBASSADOR_FIELD_BUSINESS_NAME) ||
    query.has(AMBASSADOR_FIELD_UTM_MEDIUM) ||
    query.has(AMBASSADOR_FIELD_RELATIONSHIP_OWNER_UUID) ||
    query.has(AMBASSADOR_FIELD_GAIA_EMAIL)
  )
}

/**
 * Create a new ambassador.
 *
 * @param {Object} options The options
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String} options.server The server url
 * @param {Object} options.body The ambassador's data
 */
export async function createAmbassador ({ auth, server, body }) {
  const res = await request
    .post(`${server}/v1/ambassador`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Update an ambassador.
 *
 * @param {Object} options The options
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String} options.server The server url
 * @param {Object} options.body The ambassador's data
 * @param {String} options.uuid The ambassador uuid
 */
export async function updateAmbassador ({ auth, server, body, uuid }) {
  if (!uuid) {
    throw new Error('An Partner uuid is required.')
  }

  const res = await request
    .put(`${server}/v1/ambassador/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Get a list of ambassadors from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth An immutable map with the auth data
 * @param {Number} options.page The page number
 * @param {Number} options.perPage The number of items per page
 * @param {String} options.language The language to filter by
 */
export async function getAmbassadorList (options = {}) {
  const {
    auth,
    server,
    page,
    perPage,
    sortField,
    sortDirection,
    name,
    utmMedium,
    contactEmail,
    businessName,
    gaiaEmail,
    relationshipOwnerUuid,
    pageResults,
    parentUtm,
    activeOnly,
  } = options
  const params = _omitBy(
    {
      page,
      perPage,
      pageResults,
      sortField,
      sortDirection,
      name,
      utmMedium,
      contactEmail,
      businessName,
      gaiaEmail,
      relationshipOwnerUuid,
      parentUtm,
      activeOnly,
    },
    _isUndefined,
  )
  const res = await request
    .get(`${server}/v1/ambassadors`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(params)
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get an ambassador from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.ambassadorUuid The userInfoUuid (uuid) for the ambassador
 */
export async function getAmbassador (options = {}) {
  const { auth, server, ambassadorUuid } = options

  const res = await request
    .get(`${server}/v1/ambassador`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query({ ambassadorUuid })
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get an ambassador payout from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.ambassadorUuid The userInfoUuid (uuid) for the ambassador
 * @param {String} options.startDate The startDate for the query
 * @param {String} options.endDate The endDate for the query
 */
export async function getAmbassadorPayout (options = {}) {
  const { auth, server, endDate, startDate, ambassadorUuid } = options

  const res = await request
    .get(`${server}/v1/ambassador/payouts`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query({ ambassadorUuid, startDate, endDate })
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get ambassador(s) campaigns from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String[]} options.uuids One or more ambassador uuids
 * @param {Number} options.pp records per page
 * @param {Number} options.p page number
 */
export async function getAmbassadorCampaignsMetrics (options = {}) {
  const {
    minStartDate,
    maxStartDate,
    uuid,
    ambassadorUuid,
    byAmbassador,
    utmCampaign,
    auth,
    server,
  } = options

  const res = await request
    .get(`${server}/v1/ambassadors/campaigns/metrics`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(
      _omitBy(
        {
          minStartDate,
          maxStartDate,
          byAmbassador,
          utmCampaign,
          uuid,
          ambassadorUuid,
        },
        _isUndefined,
      ),
    )
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get an ambassador payout from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.utmCampaign The utmCampaign
 */
export async function getAmbassadorCampaignDetails (options = {}) {
  const { auth, server, utmCampaign } = options

  const res = await request
    .get(`${server}/v1/ambassadors/campaigns`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query({ utmCampaign })
  return fromJS(_get(res, 'body', {}))
}

/**
 * Create a new campaign
 *
 * @param {Object} options The options
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String} options.server The server url
 * @param {Object} options.body The campign's data
 */
export async function createAmbassadorCampaign ({ auth, server, body }) {
  const res = await request
    .post(`${server}/v1/ambassadors/campaigns`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}

/**
 * Update campaign
 *
 * @param {Object} options The options
 * @param {Map} options.auth An immutable map with the auth data
 * @param {String} options.server The server url
 * @param {Object} options.body The campign's data
 */
export async function updateAmbassadorCampaign ({ auth, server, body }) {
  const { uuid } = body
  const res = await request
    .put(`${server}/v1/ambassadors/campaigns/${uuid}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
    .send(body)
  return _get(res, 'body', {})
}
/**
 * Get ambassador(s) signups from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String[]} options.uuids One or more ambassador uuids
 * @param {Number} options.pp records per page
 * @param {Number} options.p page number
 */
export async function getAmbassadorSignups (options = {}) {
  const { pp = 25, p = 1, startDate, endDate, uuids = [], utmCampaign, auth, server } = options
  const uuid =
    uuids && uuids.length
      ? _join(
          _map(uuids, (u) => `uuid[]=${u}`),
          '&',
        )
      : ''
  const res = await request
    .get(`${server}/v1/ambassadors/signups`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(uuid)
    .query(
      _omitBy(
        {
          pp,
          p,
          startDate,
          endDate,
          utmCampaign,
        },
        _isUndefined,
      ),
    )
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get ambassador(s) conversions from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String[]} options.uuids One or more ambassador uuids
 * @param {Number} options.pp records per page
 * @param {Number} options.p page number
 */
export async function getAmbassadorConversions (options = {}) {
  const { pp = 25, p = 1, startDate, endDate, uuids = [], auth, server, utmCampaign } = options
  const uuid =
    uuids && uuids.length
      ? _join(
          _map(uuids, (u) => `uuid[]=${u}`),
          '&',
        )
      : ''
  const res = await request
    .get(`${server}/v1/ambassadors/conversions`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(uuid)
    .query(
      _omitBy(
        {
          pp,
          p,
          startDate,
          endDate,
          utmCampaign,
        },
        _isUndefined,
      ),
    )
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get ambassador(s) leads from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map|null} options.auth The auth Map
 * @param {String} options.ambassadorUuid Ambassador uuid
 * @param {String} options.utmCampaign Utm Campaign
 * @param {String} options.campaignUuid Campaign uuid
 * @param {Boolean} options.pageResults Whether or not to do paging
 * @param {Number} options.perPage records per page
 * @param {Number} options.page page number
 */
export async function getAmbassadorLeads (options = {}) {
  const {
    pp = 100,
    p = 1,
    sortDirection,
    sortField,
    ambassadorUuid,
    utmCampaign,
    campaignUuid,
    pageResults,
    minCreatedAt,
    maxCreatedAt,
    auth,
    server,
  } = options

  const res = await request
    .get(`${server}/v1/ambassadors/leads`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(
      _omitBy(
        {
          perPage: pp,
          page: p > 0 ? p - 1 : p,
          sortDirection,
          sortField,
          ambassadorUuid,
          utmCampaign,
          campaignUuid,
          pageResults,
          minCreatedAt,
          maxCreatedAt,
        },
        _isUndefined,
      ),
    )

  return fromJS(_get(res, 'body', {}))
}

/**
 * Given state create the ambassador list options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorLeadsViewOptions (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const query = getLocationQuery(location)

  const [minCreatedAt, maxCreatedAt] = getStartAndEndDates({
    month: query.get('month'),
    year: query.get('year', currentYear),
    timeframe: query.get('month') ? TIMEFRAMES.MONTHLY : TIMEFRAMES.YEARLY,
  })

  return Map({
    pp: query.get('pp') || 100,
    p: query.get('p') || 1,
    ambassadorUuid: query.get('uuid'),
    utmCampaign: query.get('utmCampaign'),
    campaignUuid: query.get('campaignUuid'),
    sortDirection: query.get('sortDirection'),
    sortField: query.get('sortField'),
    minCreatedAt,
    maxCreatedAt,
  })
}

/**
 * Given state create the ambassador list options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorListViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    perPage: query.get('pp') || AMBASSADOR_ITEMS_PER_PAGE,
    sortField: query.get('sortField') || AMBASSADOR_FIELD_NAME,
    sortDirection: query.get('sortDirection') || SORT_ORDER_ASC,
    relationshipOwnerUuid: query.get('relationshipOwnerUuid'),
    parentUtm: query.get('parentUtm'),
    contactEmail: query.get('contactEmail'),
    businessName: query.get('businessName'),
    gaiaEmail: query.get('gaiaEmail'),
    utmMedium: query.get('utmMedium'),
    page: query.get('p', 1) - 1,
    name: query.get('name'),
  })
}

/**
 * Given state create the ambassador detail options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorDetailViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)

  return Map(
    _omitBy(
      {
        uuid: query.get('uuid'),
      },
      _isUndefined,
    ),
  )
}

/**
 * Given state create the ambassador detail options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorPayoutViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  const [startDate, endDate] = getStartAndEndDates({
    month: location.getIn(['data', 'query', 'month']),
    year: location.getIn(['data', 'query', 'year']),
    timeframe: TIMEFRAMES.YEARLY,
  })
  return Map(
    _omitBy(
      {
        uuid: query.get('uuid'),
        startDate,
        endDate,
      },
      _isUndefined,
    ),
  )
}

/**
 * Given state create the ambassador campaigns options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorCampaignsOptions (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const query = getLocationQuery(location)
  const year = query.get('year') || currentYear
  const ambassadorUuid = query.get('uuid')
  const utmCampaign = query.get('utmCampaign')
  const sortDirection = query.get('sortDirection')
  const sortField = query.get('sortField')

  let startDate
  let endDate

  if (year && !utmCampaign) {
    const [start, end] = getStartAndEndDates({
      timeframe: TIMEFRAMES.YEARLY,
      year,
    })
    startDate = start
    endDate = end
  }

  return Map({
    ambassadorUuid,
    utmCampaign,
    startDate,
    endDate,
    sortDirection,
    sortField,
  })
}

export function getSubHeaderCampaignsMetricsTableData (records = List(), firstColumn) {
  const subHeaderKeys = [
    CAMPAIGN_FIELD_CLICKS,
    CAMPAIGN_FIELD_LEADS,
    CAMPAIGN_FIELD_SIGNUPS,
    CAMPAIGN_FIELD_BILLINGS,
    CAMPAIGN_FIELD_REVENUE,
    CAMPAIGN_FIELD_EARNINGS,
    CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
    CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
    CAMPAIGN_FIELD_CLICK_TO_LEAD,
    CAMPAIGN_FIELD_LEAD_TO_SIGNUP,
  ]

  const truncationKeys = [
    CAMPAIGN_FIELD_EARNINGS,
    CAMPAIGN_FIELD_EARNINGS_PER_CLICK,
    CAMPAIGN_FIELD_EARNINGS_PER_LEADS,
  ]

  let subHeader = Map({
    ...firstColumn,
  })

  records.forEach((record, index) => {
    subHeaderKeys.forEach((key) => {
      let keyValue = Number(subHeader.get(key, 0)) + Number(record.get(key, 0))

      if (index === record.size - 1 && truncationKeys.includes(key)) {
        keyValue = Math.floor(keyValue * 100) / 100
      }

      subHeader = subHeader.set(key, keyValue)
    })
  })

  const totalClicks = subHeader.get(CAMPAIGN_FIELD_CLICKS)
  const totalLeads = subHeader.get(CAMPAIGN_FIELD_LEADS)
  const totalSignups = subHeader.get(CAMPAIGN_FIELD_SIGNUPS)
  const totalEarnings = subHeader.get(CAMPAIGN_FIELD_EARNINGS)

  // these fields are percantage
  subHeader = subHeader
    .set(CAMPAIGN_FIELD_CLICK_TO_LEAD, (totalLeads / totalClicks) * 100)
    .set(CAMPAIGN_FIELD_LEAD_TO_SIGNUP, (totalSignups / totalLeads) * 100)
    .set(CAMPAIGN_FIELD_EARNINGS_PER_CLICK, totalEarnings / totalClicks)
    .set(CAMPAIGN_FIELD_EARNINGS_PER_LEADS, totalEarnings / totalLeads)

  return subHeader
}

export function exportCSVCampaigns (records, year, ambassador = Map()) {
  const fullName = ambassador.get('name', 'Master').split(' ').join('')

  const title = `${fullName}-Campaigns-${year}-All_Downloaded-${formatWrapped(new Date(), 'yyyy-MM-dd')}`
  const headers = {
    [CAMPAIGN_FIELD_NAME]: 'Campaign',
    [CAMPAIGN_FIELD_CLICKS]: 'Page Views',
    [CAMPAIGN_FIELD_LEADS]: 'Leads',
    [CAMPAIGN_FIELD_SIGNUPS]: 'Signups',
    [CAMPAIGN_FIELD_BILLINGS]: 'Billings',
    [CAMPAIGN_FIELD_REVENUE]: 'Revenue',
    [CAMPAIGN_FIELD_EARNINGS]: 'Earnings',
    [CAMPAIGN_FIELD_EARNINGS_PER_CLICK]: 'Earnings per click',
    [CAMPAIGN_FIELD_EARNINGS_PER_LEADS]: 'Earnings per lead',
    [CAMPAIGN_FIELD_CLICK_TO_LEAD]: 'Click to lead CR',
    [CAMPAIGN_FIELD_LEAD_TO_SIGNUP]: 'Lead to Signup CR',
  }

  const csvData = records
    .map((record) => ({
      [CAMPAIGN_FIELD_NAME]: record.get(CAMPAIGN_FIELD_NAME, ''),
      [CAMPAIGN_FIELD_CLICKS]: record.get(CAMPAIGN_FIELD_CLICKS, 0),
      [CAMPAIGN_FIELD_LEADS]: record.get(CAMPAIGN_FIELD_LEADS, 0),
      [CAMPAIGN_FIELD_SIGNUPS]: record.get(CAMPAIGN_FIELD_SIGNUPS, 0),
      [CAMPAIGN_FIELD_BILLINGS]: record.get(CAMPAIGN_FIELD_BILLINGS, 0),
      [CAMPAIGN_FIELD_REVENUE]: getCSVNumberValue(record.get(CAMPAIGN_FIELD_REVENUE)),
      [CAMPAIGN_FIELD_EARNINGS]: getCSVNumberValue(record.get(CAMPAIGN_FIELD_EARNINGS)),
      [CAMPAIGN_FIELD_EARNINGS_PER_CLICK]: getCSVNumberValue(
        record.get(CAMPAIGN_FIELD_EARNINGS_PER_CLICK),
      ),
      [CAMPAIGN_FIELD_EARNINGS_PER_LEADS]: getCSVNumberValue(
        record.get(CAMPAIGN_FIELD_EARNINGS_PER_LEADS),
      ),
      [CAMPAIGN_FIELD_CLICK_TO_LEAD]: `${record.get(CAMPAIGN_FIELD_CLICK_TO_LEAD, 0)}%`,
      [CAMPAIGN_FIELD_LEAD_TO_SIGNUP]: `${record.get(CAMPAIGN_FIELD_LEAD_TO_SIGNUP, 0)}%`,
    }))
    .toJS()

  exportCSVFile(headers, csvData, title)
}

/**
 * Download Campaign Partners CSV file
 * @param {List} records The partners list
 * @param {String} utmCampaign The utmCampaign
 */
export function exportCSVCampaignPartners (records, utmCampaign) {
  const title = `${utmCampaign}-Campaign-Partners-${formatWrapped(new Date(), 'yyyy-MM-dd')}`
  const headers = {
    [CAMPAIGN_FIELD_PARTNER_NAME]: 'Partner',
    [CAMPAIGN_FIELD_CLICKS]: 'Page Views',
    [CAMPAIGN_FIELD_LEADS]: 'Leads',
    [CAMPAIGN_FIELD_SIGNUPS]: 'Signups',
    [CAMPAIGN_FIELD_BILLINGS]: 'Billings',
    [CAMPAIGN_FIELD_REVENUE]: 'Revenue',
    [CAMPAIGN_FIELD_EARNINGS]: 'Earnings',
    [CAMPAIGN_FIELD_EARNINGS_PER_CLICK]: 'Earnings per click',
    [CAMPAIGN_FIELD_EARNINGS_PER_LEADS]: 'Earnings per lead',
    [CAMPAIGN_FIELD_CLICK_TO_LEAD]: 'Click to lead CR',
    [CAMPAIGN_FIELD_LEAD_TO_SIGNUP]: 'Lead to Signup CR',
  }

  const csvData = records
    .map((record) => ({
      [CAMPAIGN_FIELD_PARTNER_NAME]: record.get(CAMPAIGN_FIELD_PARTNER_NAME, ''),
      [CAMPAIGN_FIELD_CLICKS]: record.get(CAMPAIGN_FIELD_CLICKS, 0),
      [CAMPAIGN_FIELD_LEADS]: record.get(CAMPAIGN_FIELD_LEADS, 0),
      [CAMPAIGN_FIELD_SIGNUPS]: record.get(CAMPAIGN_FIELD_SIGNUPS, 0),
      [CAMPAIGN_FIELD_BILLINGS]: record.get(CAMPAIGN_FIELD_BILLINGS, 0),
      [CAMPAIGN_FIELD_REVENUE]: getCSVNumberValue(record.get(CAMPAIGN_FIELD_REVENUE)),
      [CAMPAIGN_FIELD_EARNINGS]: getCSVNumberValue(record.get(CAMPAIGN_FIELD_EARNINGS)),
      [CAMPAIGN_FIELD_EARNINGS_PER_CLICK]: getCSVNumberValue(
        record.get(CAMPAIGN_FIELD_EARNINGS_PER_CLICK),
      ),
      [CAMPAIGN_FIELD_EARNINGS_PER_LEADS]: getCSVNumberValue(
        record.get(CAMPAIGN_FIELD_EARNINGS_PER_LEADS),
      ),
      [CAMPAIGN_FIELD_CLICK_TO_LEAD]: `${record.get(CAMPAIGN_FIELD_CLICK_TO_LEAD, 0)}%`,
      [CAMPAIGN_FIELD_LEAD_TO_SIGNUP]: `${record.get(CAMPAIGN_FIELD_LEAD_TO_SIGNUP, 0)}%`,
    }))
    .toJS()

  exportCSVFile(headers, csvData, title)
}

/**
 * Download Campaign Partner Contacts CSV file
 * @param {List} partners The partners list
 * @param {String} campaignName Name og the Campaign
 */
export function exportCSVCampaignPartnerContacts (partners, campaignName) {
  const title = `${campaignName}_partner_contact_list_${formatWrapped(new Date(), 'yyyy-MM-dd')}`

  const lpMaxCount = partners.reduce((acc, partner) => {
    const landingPages = partner.get('landingPages') || List()

    return landingPages.size > acc ? landingPages.size : acc
  }, 0)

  const lpHeaderNames = Array(lpMaxCount)
    .fill(null)
    .map((_, index) => `LP link ${index + 1}`)

  const getLpData = (data = []) =>
    data.reduce((acc, item, index) => {
      acc[`lp${index}`] = item

      return acc
    }, {})

  const headers = {
    name: 'Partner name',
    contactEmail: 'Partner contact email',
    ...getLpData(lpHeaderNames),
  }

  const csvData = partners
    .map((partner) => ({
      name: partner.get('name'),
      contactEmail: partner.get('contactEmail'),
      ...getLpData(partner.get('landingPages')),
    }))
    .toJS()

  exportCSVFile(headers, csvData, title)
}

/**
 * Given state create the ambassador signup options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorSignupOptions (state = {}) {
  const { location, useDefaults = false } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  const pp = useDefaults ? 25 : query.get('pp', 25)
  const p = useDefaults ? 1 : query.get('p', 1)
  const month = query.get('month')
  const year = query.get('year')
  const [startDate, endDate] = getStartAndEndDates({
    timeframe: month ? TIMEFRAMES.MONTHLY : TIMEFRAMES.YEARLY,
    month,
    year,
  })
  return Map({
    uuid: query.get('uuid'),
    utmCampaign: query.get('utmCampaign'),
    pp: parseInt(pp, 10),
    p: parseInt(p, 10),
    startDate,
    endDate,
  })
}

/**
 * Given state create the ambassador conversions options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorConversionsOptions (state = {}) {
  const { location, useDefaults = false } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  const pp = useDefaults ? 25 : query.get('pp', 25)
  const p = useDefaults ? 1 : query.get('p', 1)
  const month = query.get('month')
  const year = query.get('year')
  const [startDate, endDate] = getStartAndEndDates({
    timeframe: month ? TIMEFRAMES.MONTHLY : TIMEFRAMES.YEARLY,
    month,
    year,
  })
  return Map({
    uuid: query.get('uuid'),
    utmCampaign: query.get('utmCampaign'),
    pp: parseInt(pp, 10),
    p: parseInt(p, 10),
    startDate,
    endDate,
  })
}

/**
 * Given state create the ambassador payouts page options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createAmbassadorPayoutsPageOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  const month = query.get('month')
  const year = query.get('year')
  const [startDate, endDate] = getStartAndEndDates({
    timeframe: month ? TIMEFRAMES.MONTHLY : TIMEFRAMES.YEARLY,
    month,
    year,
  })
  return Map({
    endDate,
    startDate,
    page: query.get('p', 1) - 1,
    perPage: query.get('perPage') || AMBASSADOR_ITEMS_PER_PAGE,
    sortField: query.get('sortField') || AMBASSADOR_FIELD_UTM_MEDIUM,
    sortDirection: query.get('sortDirection') || SORT_ORDER_ASC,
    name: query.get('name'),
    utmMedium: query.get('utmMedium'),
    contactEmail: query.get('contactEmail'),
    gaiaEmail: query.get('gaiaEmail'),
    businessName: query.get('businessName'),
    relationshipOwnerUuid: query.get('relationshipOwnerUuid'),
    parentUtm: query.get('parentUtm'),
  })
}

/**
 * Return true when in ambassador list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/ambassadors'
 */
export function isAmbassadorListPage (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/ambassadors',
  })
}

/**
 * Return true when in ambassador detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/ambassadors/detail'
 */
export function isAmbassadorDetailPage (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/ambassadors/detail',
  })
}

/**
 * Return true when in ambassador payouts page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/ambassadors/payouts'
 */
export function isAmbassadorPayoutsPage (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/ambassadors/payouts',
  })
}

/**
 * Get a list of ambassador payouts from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.startDate The date at which to start collecting payout information
 * @param {String} options.endDate The date at which to end collecting payout information
 */
export async function getAmbassadorPayouts (options = {}) {
  const {
    auth,
    server,
    startDate,
    endDate,
    name,
    utmMedium,
    businessName,
    contactEmail,
    gaiaEmail,
    sortField,
    sortDirection,
    page,
    perPage,
    pageResults,
    relationshipOwnerUuid,
    parentUtm,
  } = options

  const params = _omitBy(
    {
      startDate,
      endDate,
      name,
      utmMedium,
      businessName,
      contactEmail,
      gaiaEmail,
      sortField,
      sortDirection,
      page,
      perPage,
      pageResults,
      relationshipOwnerUuid,
      parentUtm,
    },
    _isUndefined,
  )

  const res = await request
    .get(`${server}/v1/ambassadors/payouts`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(params)
  return fromJS(_get(res, 'body', {}))
}

/**
 * Get the ambassador totals from brooklyn
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {String} options.startDate The date at which to start collecting payout information
 * @param {String} options.endDate The date at which to end collecting payout information
 */
export async function getAmbassadorPayoutTotals (options = {}) {
  const {
    auth,
    server,
    startDate,
    endDate,
    name,
    utmMedium,
    businessName,
    contactEmail,
    gaiaEmail,
    sortField,
    sortDirection,
    page,
    perPage,
  } = options

  const params = _omitBy(
    {
      startDate,
      endDate,
      name,
      utmMedium,
      businessName,
      contactEmail,
      gaiaEmail,
      sortField,
      sortDirection,
      page,
      perPage,
    },
    _isUndefined,
  )

  const res = await request
    .get(`${server}/v1/ambassadors/payouts/totals`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(params)
  return fromJS(_get(res, 'body', {}))
}

export async function getAmbassadorHistory (options = {}) {
  const { userUuid, ambassadorsOnly, server, auth } = options
  const res = await request
    .post(`${server}/v1/audit/search`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .send({
      ambassadorsOnly,
      userUuid,
    })
  return fromJS(_get(res, 'body', {}))
}

export async function getAmbassadorAdmins (options = {}) {
  const { server, auth } = options
  const res = await request
    .get(`${server}/v1/user/list`)
    // .query({ uid: uid.toSet().toList().join(',') })
    .query({
      filter: { user_role: { val: 43, op: 'eq' } },
    })
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(_get(res, 'body', null))
}

export async function getAmbassadorRelationshipOwners (options = {}) {
  const { server, auth } = options
  const res = await request
    .get(`${server}/v1/ambassador/admins`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(_get(res, 'body', null))
}

/**
 * Determine whether or not a payout record is below PAYOUT_MIN or ambassador is inctive
 * @param {Number} num The amount
 * @param {Boolean} active The active bool
 * @returns bool
 */
export function isLowOrInactive (num, active) {
  if (!isBoolean(active)) return false
  return num < PAYOUT_MIN || !active
}

/**
 * Get and format the total monthy payout
 * @param {Number} totalPayoutUSD
 * @param {Number} totalUnpaidUSD
 * @returns {String}
 */
export function getTotalPayoutUSD (totalPayoutUSD, totalUnpaidUSD) {
  if (isNumber(totalPayoutUSD) && isNumber(totalUnpaidUSD)) {
    return formatAmount(totalPayoutUSD + totalUnpaidUSD)
  }
  return '--'
}

export function getPlan (record) {
  const contentSetId = record.get('planContentSetId', 0)
  return (
    (contentSetId === 290 && ('Gaia+' || 'Events+')) ||
    (contentSetId === 280 && 'Annual') ||
    (contentSetId === 253 && 'Monthly') ||
    'N/A'
  )
}

/**
 * Return true when in campaing detail page
 * @param {Array} columns Array of columns
 * @param {String} columnKey Array of columns
 * @returns {Array} Array of columns without removing by key column
 */
export function removeColumnByKey (columns, columnKey) {
  const currentColumns = columns.slice()

  if (!columnKey) {
    return currentColumns
  }

  const columnIndex = currentColumns.findIndex(({ key }) => key === columnKey)

  if (columnIndex !== -1) {
    currentColumns.splice(columnIndex, 1)
  }

  return currentColumns
}

/**
 * Return true when in campaing detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/ambassadors/payouts'
 */
export function isAmbassadorCampaignDetailPage (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/campaign-detail',
  })
}

/**
 * Return true when in campaigns metric page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/ambassadors/payouts'
 */
export function isAmbassadorCampaignMetricsPage (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const isCampaignDetailPage = !!matchPath({
    pathname: getLocationPathname(location),
    path: '/campaign-detail',
  })

  const isAmbassadorsPage = !!matchPath({
    pathname: getLocationPathname(location),
    path: '/ambassadors',
  })

  const isAmbassadorsDetailPage = !!matchPath({
    pathname: getLocationPathname(location),
    path: '/ambassadors/detail',
  })

  const tab = location.getIn(['data', 'query', 'tab'])

  return (
    (isCampaignDetailPage && tab === CAMPAIGN_PARTNERS_TAB) ||
    (isAmbassadorsPage && (!tab || tab === AMBASSADOR_CAMPAIGNS_TAB)) ||
    (isAmbassadorsDetailPage && tab === AMBASSADOR_CAMPAIGNS_TAB)
  )
}

export function isLeadsTab (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const tab = location.getIn(['data', 'query', 'tab'])

  return tab === LEADS_TAB
}

export function isSignupsTab (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const tab = location.getIn(['data', 'query', 'tab'])

  return tab === SIGNUPS_TAB
}

export function isBillingsTab (state = {}) {
  const { location } = state

  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }

  const tab = location.getIn(['data', 'query', 'tab'])

  return tab === BILLINGS_TAB
}
